import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';

type Locale = 'hr' | 'en';
type ActivityRow = { id: number; ts: string | null; ip: string; method: string; path: string; query: string; body: string; status: number | null; user_agent: string; auth_user: string };
type Stats = {
    empty: boolean; total?: number; by_status?: Record<string, number>; methods?: Record<string, number>;
    errors_4xx_5xx?: number; login_posts?: number; unique_ips?: number;
    top_paths?: Array<{ path: string; n: number }>; top_ips?: Array<{ ip: string; n: number; last: string }>;
    range?: { a: string; b: string }; signals?: Array<{ type: string; count: number }>; last_ingest?: string | null;
};
type Assessment = { id: number; generated_at: string; verdict: string; event_count: number; summary: string; signals: string };

const VERDICT: Record<string, { label: string; bg: string; fg: string; icon: string }> = {
    holding: { label: 'DRŽI', bg: 'rgba(31,122,54,0.14)', fg: '#1f7a36', icon: '🛡' },
    probing: { label: 'SONDIRA', bg: 'rgba(var(--ad-acc-rgb),0.16)', fg: 'var(--ad-acc-3)', icon: '🔍' },
    breach_suspected: { label: 'MOGUĆ PROBOJ', bg: 'rgba(177,64,64,0.14)', fg: '#b14040', icon: '🚨' },
    inconclusive: { label: 'NEODREĐENO', bg: 'rgba(120,113,90,0.16)', fg: '#6b5f3a', icon: '❔' },
};

export function PentestMonitorModule({ token, locale, onBack }: { token: string; locale: Locale; onBack: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [stats, setStats] = useState<Stats | null>(null);
    const [rows, setRows] = useState<ActivityRow[]>([]);
    const [assessments, setAssessments] = useState<Assessment[]>([]);
    const [q, setQ] = useState('');
    const [onlyErr, setOnlyErr] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const load = useCallback(async () => {
        setErr('');
        try {
            const [s, a, h] = await Promise.all([
                api<Stats>('/pentest-monitor/stats', token),
                api<{ rows: ActivityRow[] }>(`/pentest-monitor/activity?limit=300${onlyErr ? '&only_errors=1' : ''}${q ? '&q=' + encodeURIComponent(q) : ''}`, token),
                api<{ rows: Assessment[] }>('/pentest-monitor/assessments', token),
            ]);
            setStats(s); setRows(a.rows ?? []); setAssessments(h.rows ?? []);
        } catch (e) { setErr(e instanceof Error ? e.message : 'API'); }
    }, [token, q, onlyErr]);
    useEffect(() => { void load(); }, [load]);
    useEffect(() => { const id = setInterval(() => { void load(); }, 60000); return () => clearInterval(id); }, [load]); // auto-refresh 60s

    const assess = async () => {
        setBusy(true); setErr('');
        try { await mutate('/pentest-monitor/assess', token, 'POST', {}); await load(); }
        catch (e) { setErr(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const card = (label: string, value: React.ReactNode, color = 'var(--ad-ink)') => (
        <div style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '10px 14px', background: '#fffdf6', minWidth: 120 }}>
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ad-acc-3)' }}>{label}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color }}>{value}</div>
        </div>
    );
    const fmtTs = (ts: string | null) => ts ? new Date(ts.replace(' ', 'T')).toLocaleString('hr-HR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
    const statusColor = (s: number | null) => !s ? '#888' : s >= 500 ? '#b14040' : s >= 400 ? '#c98a31' : s >= 300 ? '#6b87a8' : '#1f7a36';
    const lastAssess = assessments[0];

    return (
        <section>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <button type="button" className="secondary compact" onClick={onBack}>← {t('Natrag', 'Back')}</button>
                <h2 style={{ margin: 0, color: 'var(--ad-ink)', flex: 1 }}>🛡 Pentest Monitor <span className="muted" style={{ fontSize: '0.8rem', fontWeight: 400 }}>pentest.aurodomus.eu</span></h2>
                <button type="button" className="secondary compact" onClick={() => void load()}>🔄 {t('Osvježi', 'Refresh')}</button>
                <button type="button" disabled={busy} onClick={() => void assess()} style={{ background: 'linear-gradient(135deg,var(--ad-ink),var(--ad-acc))', color: '#fffbeb', border: 0, padding: '8px 16px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>{busy ? '⏳ ' + t('Analiziram…', 'Analyzing…') : '✨ ' + t('AI procjena', 'AI assessment')}</button>
            </div>
            {err ? <p className="error">{err}</p> : null}

            {/* AI verdikt */}
            {lastAssess ? (() => {
                const v = VERDICT[lastAssess.verdict] ?? VERDICT.inconclusive;
                let sig: any = {}; try { sig = JSON.parse(lastAssess.signals || '{}'); } catch { /* */ }
                const recs: string[] = sig._recommendations ?? [];
                const techs: string[] = sig._techniques ?? [];
                return (
                    <div style={{ background: v.bg, border: `1px solid ${v.fg}55`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '1.6rem' }}>{v.icon}</span>
                            <strong style={{ fontSize: '1.2rem', color: v.fg }}>{v.label}</strong>
                            <span className="muted" style={{ fontSize: '0.8rem' }}>{t('procjena', 'assessed')} {fmtTs(lastAssess.generated_at)} · {lastAssess.event_count} {t('zahtjeva', 'requests')}</span>
                        </div>
                        {lastAssess.summary ? <p style={{ margin: '8px 0 0', color: '#2a2417' }}>{lastAssess.summary}</p> : null}
                        {techs.length ? <div style={{ marginTop: 8, fontSize: '0.84rem' }}><strong>{t('Tehnike', 'Techniques')}:</strong> {techs.join(', ')}</div> : null}
                        {recs.length ? <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: '0.84rem' }}>{recs.map((r, i) => <li key={i}>{r}</li>)}</ul> : null}
                    </div>
                );
            })() : null}

            {/* Statistika */}
            {stats && !stats.empty ? (
                <>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                        {card(t('Zahtjeva', 'Requests'), (stats.total ?? 0).toLocaleString('hr-HR'))}
                        {card(t('Greške 4xx/5xx', 'Errors'), stats.errors_4xx_5xx ?? 0, '#b14040')}
                        {card(t('Login POST', 'Login POST'), stats.login_posts ?? 0, '#c98a31')}
                        {card(t('Različitih IP', 'Unique IPs'), stats.unique_ips ?? 0)}
                        {card(t('Crveni signali', 'Red signals'), (stats.signals?.length ?? 0), (stats.signals?.length ?? 0) > 0 ? '#b14040' : '#1f7a36')}
                    </div>
                    {(stats.signals?.length ?? 0) > 0 ? (
                        <div style={{ background: 'rgba(177,64,64,0.08)', border: '1px solid rgba(177,64,64,0.3)', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
                            <strong style={{ color: '#b14040' }}>⚠ {t('Detektirani obrasci napada', 'Detected attack patterns')}:</strong>{' '}
                            {stats.signals!.map((s) => `${s.type} (${s.count})`).join(' · ')}
                        </div>
                    ) : null}
                    <div className="muted" style={{ fontSize: '0.78rem', marginBottom: 10 }}>
                        {t('Raspon', 'Range')}: {fmtTs(stats.range?.a ?? null)} – {fmtTs(stats.range?.b ?? null)} · {t('zadnji uvoz', 'last ingest')}: {fmtTs(stats.last_ingest ?? null)}
                    </div>
                </>
            ) : stats?.empty ? <p className="muted" style={{ fontStyle: 'italic' }}>{t('Još nema podataka. Hourly cron (min :17) uvozi log s pentest-kutije — ili pričekaj prvi push.', 'No data yet. Hourly cron ingests the log.')}</p> : null}

            {/* Filtri + tablica */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void load(); }} placeholder={t('traži po putanji/tijelu/query…', 'search path/body/query…')}
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)' }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.84rem' }}><input type="checkbox" checked={onlyErr} onChange={(e) => setOnlyErr(e.target.checked)} /> {t('samo greške', 'errors only')}</label>
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 8, maxHeight: '60vh', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead><tr style={{ background: 'rgba(var(--ad-acc-rgb),0.12)', position: 'sticky', top: 0 }}>
                        <th style={{ textAlign: 'left', padding: '5px 8px' }}>{t('Vrijeme', 'Time')}</th>
                        <th style={{ textAlign: 'left', padding: '5px 8px' }}>IP</th>
                        <th style={{ textAlign: 'left', padding: '5px 8px' }}>{t('Metoda', 'Method')}</th>
                        <th style={{ textAlign: 'left', padding: '5px 8px' }}>{t('Putanja', 'Path')}</th>
                        <th style={{ textAlign: 'right', padding: '5px 8px' }}>St.</th>
                    </tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} style={{ borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.12)' }} title={(r.query ? '?' + r.query : '') + (r.body ? '\n' + r.body : '') + '\n' + r.user_agent}>
                                <td style={{ padding: '4px 8px', whiteSpace: 'nowrap', color: '#6b5f3a' }}>{fmtTs(r.ts)}</td>
                                <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{r.ip}</td>
                                <td style={{ padding: '4px 8px', fontWeight: 700 }}>{r.method}</td>
                                <td style={{ padding: '4px 8px', maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.path}{r.query ? <span className="muted">?{r.query.slice(0, 80)}</span> : null}</td>
                                <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700, color: statusColor(r.status) }}>{r.status ?? ''}</td>
                            </tr>
                        ))}
                        {rows.length === 0 ? <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#888', fontStyle: 'italic' }}>{t('Nema zapisa.', 'No records.')}</td></tr> : null}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
